import debounce from 'lodash/debounce'
import throttle from 'lodash/throttle'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { useRTM } from 'features/ConferenceCall/hooks/useAgora'
import { useUserId } from 'features/ConferenceCall/hooks/useCall'
import { LocalStorage } from 'lib/storage'
import { Now } from 'lib/time'
import { useParams } from 'react-router'
import { useUpdateCallUserRoleMutation } from 'store/api/yay/call'
import {
  CallUser,
  addCallReaction,
  getLocalUserTracks,
  rtcClient,
  selectCallUsers,
  setCallUser,
} from 'store/slices/agora'
import {
  addCallChatMessage,
  deleteCallChatMessage,
  pinCallChatMessage,
  unpinCallChatMessage,
} from 'store/slices/callChat'
import { addNotification } from 'store/slices/notifications'
import { CallUserRole } from 'lib/_types'

const useConferenceCallFunctions = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { id: conferenceId } = useParams()
  const { userId: currentCallUserID } = useUserId()

  const callUsers = useSelector(selectCallUsers)

  const [updateCallUserRole] = useUpdateCallUserRoleMutation()

  const { sendChannelMessage, deleteChannelAttribute, setChannelAttributes } =
    useRTM()

  const getCallUid = (id: string) =>
    Object.values(callUsers ?? {}).find((u) => u.id === id || u.uid === id)
      ?.uid ?? id

  const sendGift = async (giftString) => {
    try {
      await sendMessageWithThrottle(`${giftString}`)
    } catch (e) {
      console.error(e)
    }
  }

  // https://jira.nanameue.jp/browse/YAY-4400
  const sendMessageWithThrottle = useCallback(
    throttle(async (message) => {
      await sendChannelMessage(message)
    }, 500),
    []
  )

  const sendMessage = async (text) => {
    const createdAt = Now.ms()
    const createdAtSeconds = Math.round(createdAt / 1000)
    const id = `${currentCallUserID}_${createdAt}`

    const messageJson = JSON.stringify({
      id,
      text,
      created_at_seconds: createdAtSeconds,
    })

    dispatch(
      addCallChatMessage({
        id,
        userId: currentCallUserID,
        text,
        createdAt,
      })
    )

    try {
      await sendChannelMessage(`chat ${messageJson}`)
    } catch (e) {
      console.error(e)
    }
  }

  const deleteMessage = async (id) => {
    dispatch(deleteCallChatMessage(id))

    try {
      await sendChannelMessage(`deleteChat ${id}`)
    } catch (e) {
      console.error(e)
    }
  }

  const pinMessage = async ({ message, nickname }) => {
    dispatch(pinCallChatMessage({ ...message, nickname }))

    const messageJson = JSON.stringify({
      id: message.id,
      message: message.text,
      nickname,
    })

    try {
      await setChannelAttributes({ pinnedChat: messageJson })
    } catch (e) {
      console.error(e)
    }
  }

  const unpinMessage = async () => {
    dispatch(unpinCallChatMessage())

    try {
      await deleteChannelAttribute('pinnedChat')
    } catch (e) {
      console.error(e)
    }
  }

  // request to host to lift audio ban
  const requestToUnLiftAudioBan = async () => {
    try {
      await sendChannelMessage('requestLiftAudioMute')
      dispatch(
        addNotification({
          message: t('conference_call_function.request_to_unmute.alert'),
        })
      )
    } catch (e) {
      console.error(e)
    }
  }

  // request to host to lift video ban
  const requestToUnLiftVideoBan = async () => {
    try {
      await sendChannelMessage('requestLiftVideoMute')
      dispatch(
        addNotification({
          message: t('conference_call_function.request_to_unmute.alert'),
        })
      )
    } catch (e) {
      console.error(e)
    }
  }

  const setPeerAudioVolume = (id: string, volume: number) => {
    const uid = getCallUid(id)
    rtcClient.remoteUsers
      .find((u) => u.uid === uid)
      ?.audioTrack?.setVolume?.(volume)

    dispatch(
      setCallUser({
        id,
        data: {
          audioVolume: volume,
        },
      })
    )
  }

  const onToggleMic = useCallback(
    // https://jira.nanameue.jp/browse/YAY-4400
    debounce(() => {
      const { audioTrack } = getLocalUserTracks()
      const isMuted = audioTrack?.muted
      audioTrack?.setMuted?.(!isMuted)
      LocalStorage.callSettings.set({
        ...LocalStorage.callSettings.get(),
        muteAudio: !isMuted,
      })
      dispatch(
        setCallUser({
          id: currentCallUserID,
          data: { muteAudio: !isMuted },
        })
      )
    }, 500),
    []
  )

  const onToggleVideo = useCallback(
    // https://jira.nanameue.jp/browse/YAY-4400
    debounce(() => {
      const { videoTrack } = getLocalUserTracks()
      const isMuted = videoTrack?.muted
      videoTrack?.setMuted?.(!isMuted)
      LocalStorage.callSettings.set({
        ...LocalStorage.callSettings.get(),
        muteVideo: !isMuted,
      })
      dispatch(
        setCallUser({
          id: currentCallUserID,
          data: { muteVideo: !isMuted },
        })
      )
    }, 500),
    []
  )

  const sendReaction = async (reaction) => {
    try {
      await sendMessageWithThrottle(`react ${reaction}`)
      dispatch(addCallReaction({ content: reaction }))
    } catch (e) {
      console.error(e)
    }
  }

  const _setUserRole = async (user: CallUser, role: CallUserRole) => {
    await updateCallUserRole({ userId: user.id, conferenceId, role })
    await sendChannelMessage(`changeUserRole ${user.uid} ${role}`)
  }

  const promoteModerator = (user: CallUser) => {
    _setUserRole(user, 'moderator')
    dispatch(
      addNotification({
        message: t('conference_call.roles.moderator_added'),
      })
    )
  }
  const demoteModerator = (user: CallUser) => {
    _setUserRole(user, 'participant')
    dispatch(
      addNotification({
        message: t('conference_call.roles.moderator_removed'),
      })
    )
  }

  return {
    setPeerAudioVolume,
    onToggleMic,
    onToggleVideo,
    sendGift,
    sendReaction,
    requestToUnLiftAudioBan,
    requestToUnLiftVideoBan,
    promoteModerator,
    demoteModerator,
    sendMessage,
    deleteMessage,
    pinMessage,
    unpinMessage,
  }
}

export default useConferenceCallFunctions
